import BrandName from "./BrandName";
import { brandText } from "../brand";
import React, { useEffect, useRef, useState } from "react";
import feast from "../assets/bg.jpeg";
import { imageUrl } from "./MenuImage";
import { calendarTimeLabel } from "./EventsCalendar";
import "./HomepageHero.css";
const welcome = {
  id: "welcome",
  label: "Welcome to nomii",
  actions: [{ label: "Explore the menu", href: "#menu-heading" }],
};
export function safeHeroLink(value) {
  if (typeof value !== "string") return null;
  if (
    /^#[a-zA-Z][\w-]*$/.test(value) ||
    /^\/\?view=list&month=20\d{2}-(0[1-9]|1[0-2])&category=[\w%-]+#whats-on$/.test(
      value,
    )
  )
    return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
const dateLabel = (date) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
function HeroPhoto({ slide }) {
  const [failed, setFailed] = useState(false);
  const base = slide.image?.baseUrl;
  const src = imageUrl(base, 1920);
  const mobile = imageUrl(slide.mobileImage?.baseUrl, 960);
  return (
    <picture
      className="feature-photo"
      style={{
        "--desktop-position": `${slide.image?.focalX ?? 50}% ${
          slide.image?.focalY ?? 50
        }%`,
        "--mobile-position": `${
          (slide.mobileImage || slide.image)?.focalX ?? 50
        }% ${(slide.mobileImage || slide.image)?.focalY ?? 50}%`,
      }}
    >
      {mobile && !failed && (
        <source media="(max-width: 700px)" srcSet={mobile} />
      )}
      <img
        src={!failed && src ? src : feast}
        srcSet={
          !failed && src
            ? [960, 1440, 1920, 2400]
                .map((w) => `${imageUrl(base, w)} ${w}w`)
                .join(", ")
            : undefined
        }
        sizes="(max-width: 700px) 100vw, 65vw"
        alt={
          !failed && src
            ? brandText(slide.image?.alt || slide.headline)
            : "Thai food at nomii"
        }
        fetchPriority="high"
        loading="eager"
        onError={() => setFailed(true)}
      />
    </picture>
  );
}
export default function HomepageHero({ data: supplied }) {
  const [data, setData] = useState(supplied || null);
  const [activeId, setActiveId] = useState(null);
  const touch = useRef(null);
  useEffect(() => {
    if (supplied !== undefined) {
      setData(supplied);
      return;
    }
    let alive = true;
    let controller;
    let generation = 0;
    const refresh = async () => {
      controller?.abort();
      controller = new AbortController();
      const current = ++generation;
      const requestController = controller;
      const timeout = window.setTimeout(() => requestController.abort(), 8000);
      try {
        const response = await fetch("/api/homepage", {
          signal: requestController.signal,
          cache: "no-store",
        });
        if (!response.ok) throw new Error();
        const body = await response.json();
        if (!body.success || !Array.isArray(body.data?.slides))
          throw new Error();
        if (alive && generation === current) setData(body.data);
      } catch {
        if (alive && generation === current) setData(null);
      } finally {
        window.clearTimeout(timeout);
      }
    };
    void refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => {
      alive = false;
      controller?.abort();
      window.clearInterval(timer);
    };
  }, [supplied]);
  const slides = data?.slides?.length ? data.slides : [welcome];
  const index = Math.max(
    0,
    slides.findIndex((slide) => slide.id === activeId),
  );
  const slide = slides[index];
  const move = (direction) =>
    setActiveId(slides[(index + direction + slides.length) % slides.length].id);
  return (
    <>
      <section
        className="homepage-feature"
        aria-label="Featured at nomii"
        aria-roledescription={slides.length > 1 ? "carousel" : undefined}
        onTouchStart={(e) => {
          touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }}
        onTouchEnd={(e) => {
          if (!touch.current || slides.length < 2) return;
          const dx = e.changedTouches[0].clientX - touch.current.x;
          const dy = e.changedTouches[0].clientY - touch.current.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy))
            move(dx < 0 ? 1 : -1);
          touch.current = null;
        }}
      >
        <h1 className="feature-sr">nomii Thai Asian Dining, Hexham</h1>
        <div className="feature-stage" aria-live="polite" aria-atomic="true">
          <HeroPhoto key={slide.id} slide={slide} />
          <div className="feature-copy" key={`copy-${slide.id}`}>
            <p className={`feature-kicker${slide.id === "welcome" ? " feature-kicker--welcome" : ""}`}>
              <span aria-hidden="true" /> {slide.id === "welcome" ? <span>Welcome to <BrandName /></span> : brandText(slide.label)}
            </p>
            {slide.headline && <h2>{brandText(slide.headline)}</h2>}
            {slide.event && (
              <p className="feature-date">
                {dateLabel(slide.event.date)} · {calendarTimeLabel(slide.event)}
              </p>
            )}
            {slide.description && (
              <p className="feature-description">{brandText(slide.description)}</p>
            )}
            <div className="feature-actions">
              {(slide.actions || []).map(
                (action, i) =>
                  safeHeroLink(action.href) && (
                    <a
                      key={i}
                      className={
                        i === 0 ? "feature-primary" : "feature-secondary"
                      }
                      href={safeHeroLink(action.href)}
                    >
                      {brandText(action.label)}
                      <span aria-hidden="true">↗</span>
                    </a>
                  ),
              )}
            </div>
            <p className="feature-location">
              THAI ASIAN DINING <span>·</span> HEXHAM
            </p>
          </div>
        </div>
        {slides.length > 1 && (
          <div className="feature-controls">
            <div className="feature-tabs" aria-label="Choose an advert">
              {slides.map((item, i) => (
                <button
                  key={item.id}
                  aria-pressed={index === i}
                  onClick={() => setActiveId(item.id)}
                >
                  <span>{String(i + 1).padStart(2, "0")}</span>
                  {brandText(item.label)}
                </button>
              ))}
            </div>
            <div className="feature-arrows">
              <button aria-label="Previous advert" onClick={() => move(-1)}>
                ←
              </button>
              <button aria-label="Next advert" onClick={() => move(1)}>
                →
              </button>
            </div>
          </div>
        )}
      </section>
      {data?.nextMusic && safeHeroLink(data.nextMusic.href) && (
        <a className="next-music" href={safeHeroLink(data.nextMusic.href)}>
          <span className="next-music-label">
            <span aria-hidden="true">♫</span> NEXT LIVE MUSIC
          </span>
          <span className="next-music-detail">
            <strong>{brandText(data.nextMusic.name)}</strong>
            <span>
              {dateLabel(data.nextMusic.date)} ·{" "}
              {calendarTimeLabel(data.nextMusic)}
            </span>
          </span>
          <span className="next-music-link">
            See what’s on <span aria-hidden="true">↗</span>
          </span>
        </a>
      )}
    </>
  );
}
